import { useQuery } from "@tanstack/react-query";
import {
  Award,
  Calendar,
  Check,
  Clock,
  Users,
} from "lucide-react";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { api } from "@/lib/api";
import { formatItalianDateShort } from "@/lib/format";
import { cn } from "@/lib/utils";
import dayjs from "dayjs";

export function PlaytomicParticipantsPanel({
  activityId,
}: {
  activityId: string;
}) {
  const q = useQuery({
    queryKey: ["playtomic-participants", activityId],
    queryFn: () => api.playtomicParticipants(activityId),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });

  if (q.isError) {
    return (
      <Card>
        <CardBody className="text-sm text-red-700">
          Errore lettura iscritti Playtomic: {String(q.error)}
        </CardBody>
      </Card>
    );
  }

  const d = q.data;
  const progressPct =
    d && d.max_players > 0
      ? Math.round((d.registered_count / d.max_players) * 100)
      : 0;
  const statusStyles =
    d?.tournament_status === "REGISTRATION_OPEN"
      ? "bg-moving-100 text-moving-700"
      : d?.tournament_status === "REGISTRATION_CLOSED"
        ? "bg-red-100 text-red-700"
        : "bg-slate-100 text-slate-600";
  const statusLabel =
    d?.tournament_status === "REGISTRATION_OPEN"
      ? "Iscrizioni aperte"
      : d?.tournament_status === "REGISTRATION_CLOSED"
        ? "Iscrizioni chiuse"
        : d?.tournament_status || "—";

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Iscritti Playtomic
            {d && d.registered_count > 0 ? (
              <span className="text-sm font-normal text-slate-500">
                · {d.registered_count}
                {d.max_players > 0 ? ` / ${d.max_players}` : ""}
              </span>
            ) : null}
          </CardTitle>
          {d ? (
            <span
              className={cn(
                "rounded px-2 py-0.5 text-xs font-medium",
                statusStyles,
              )}
            >
              {statusLabel}
            </span>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-3">
        {q.isLoading ? (
          <div className="text-sm text-slate-500">Caricamento iscritti…</div>
        ) : !d ? null : (
          <>
            {/* KPI row */}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <MiniStat
                icon={<Users className="h-3 w-3" />}
                label="Iscritti"
                value={`${d.registered_count}`}
                hint={d.max_players > 0 ? `max ${d.max_players}` : ""}
              />
              <MiniStat
                icon={<Award className="h-3 w-3" />}
                label="Livelli"
                value={d.level_description || "—"}
                hint={d.gender === "MIXED" ? "misto" : d.gender}
              />
              <MiniStat
                icon={<Clock className="h-3 w-3" />}
                label="Prezzo attuale"
                value={d.price_current || "—"}
                hint="dinamico"
              />
              <MiniStat
                icon={<Calendar className="h-3 w-3" />}
                label="Chiusura iscrizioni"
                value={
                  d.registration_closing_time
                    ? dayjs(d.registration_closing_time).format("DD/MM HH:mm")
                    : "—"
                }
              />
            </div>

            {/* Progress bar */}
            {d.max_players > 0 ? (
              <div>
                <div className="mb-1 flex items-center justify-between text-xs text-slate-500">
                  <span>Riempimento</span>
                  <span className="font-mono">
                    {d.registered_count}/{d.max_players} · {progressPct}%
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className="h-2 rounded-full bg-moving-500 transition-all"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
                {d.available_places > 0 && d.available_places <= 2 ? (
                  <div className="mt-1 text-xs text-amber-700">
                    ⚡ Ultimi {d.available_places} posti!
                  </div>
                ) : null}
              </div>
            ) : null}

            {/* Lista iscritti */}
            {d.players.length === 0 ? (
              <div className="rounded-md bg-slate-50 p-4 text-center text-sm text-slate-500">
                Nessun iscritto ancora.
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {d.players.map((p, i) => (
                  <li
                    key={p.user_id}
                    className="flex items-center gap-3 py-2"
                  >
                    <span className="w-5 shrink-0 text-center text-xs font-mono text-slate-400">
                      {i + 1}
                    </span>
                    {p.picture ? (
                      <img
                        src={p.picture}
                        alt={p.full_name}
                        className="h-8 w-8 shrink-0 rounded-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-500">
                        {p.full_name.slice(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium text-slate-900">
                        {p.full_name}
                      </div>
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        {p.level_value !== null ? (
                          <span>liv. {p.level_value.toFixed(2)}</span>
                        ) : null}
                        {p.registration_price ? (
                          <>
                            <span>·</span>
                            <span className="font-mono">
                              {p.registration_price}
                            </span>
                          </>
                        ) : null}
                        {p.payment_method_type ? (
                          <>
                            <span>·</span>
                            <span className="text-slate-400">
                              {p.payment_method_type
                                .replace("_", " ")
                                .toLowerCase()}
                            </span>
                          </>
                        ) : null}
                      </div>
                    </div>
                    {p.paid ? (
                      <span
                        className="inline-flex items-center gap-1 rounded bg-moving-100 px-1.5 py-0.5 text-[10px] font-semibold text-moving-700"
                        title="Pagato"
                      >
                        <Check className="h-3 w-3" />
                        paid
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
                        title="In attesa di pagamento"
                      >
                        pending
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}

            <div className="border-t border-slate-100 pt-2 text-[11px] text-slate-400">
              Fonte: api.playtomic.io · auto-refresh 60s · l'API pubblica non
              distingue community/non-community (il 13€ iniziale è dynamic
              pricing Playtomic, non sconto community).
            </div>
          </>
        )}
      </CardBody>
    </Card>
  );
}

function MiniStat({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md bg-slate-50 p-2.5">
      <div className="flex items-center gap-1 text-[10px] uppercase text-slate-500">
        {icon}
        {label}
      </div>
      <div className="mt-0.5 text-sm font-semibold text-slate-900">{value}</div>
      {hint ? <div className="text-[11px] text-slate-400">{hint}</div> : null}
    </div>
  );
}

// silence unused
void formatItalianDateShort;
